/** @format */

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	Injector,
	input,
	NgZone,
	OnInit,
	PLATFORM_ID
} from '@angular/core';

import { take } from 'rxjs/operators';
import { BNPLScriptData, BNPLService } from './service/bnpl.service';
import { Environment, ProductStylePrices } from '@shared/data-access/interfaces';
import { WINDOW } from '@shared/data-access/services';
import { Store } from '@ngrx/store';
import { selectResolvedSku } from '@pdp/data-access/state';

@Component({
	selector: 'pdp-bnpl',
	template: `@if (displayBNPLMessage) {
		<div
			id="bnpl-messaging-container"
			class="hmf-mx-s hmf-mx-m-0 hmf-mb-m"></div>
	}`
})
export class BNPLComponent implements OnInit {
	readonly isProductPaypalEligible = input<boolean>();
	readonly productPrice = input<ProductStylePrices>();

	public displayBNPLMessage: boolean;

	private readonly environment = inject(Environment);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly bnplService = inject(BNPLService);
	private readonly document = inject(DOCUMENT);
	protected readonly window = inject(WINDOW) as Window;
	private readonly store = inject(Store);
	private readonly injector = inject(Injector);
	private readonly ngZone = inject(NgZone);

	private readonly resolvedSku = this.store.selectSignal(selectResolvedSku);
	private readonly isPayPalEligible = computed(() =>
		this.resolvedSku() ? this.resolvedSku().isPayPalEligible : this.isProductPaypalEligible()
	);

	private mountPromise: Promise<void> = Promise.resolve();

	ngOnInit() {
		if (isPlatformBrowser(this.platformId)) {
			this.bnplService
				.getBNPLAssets$()
				.pipe(take(1))
				.subscribe({
					next: (bnplAssets) => {
						if (bnplAssets) {
							this.setBNPLScript(bnplAssets);
							this.displayBNPLMessage = true;
						} else {
							return (this.displayBNPLMessage = false);
						}
					},
					error: () => {
						this.displayBNPLMessage = false;
					}
				});
		} else {
			this.displayBNPLMessage = false;
		}
	}

	setBNPLScript(data: BNPLScriptData) {
		const bnplScript = document.createElement('script');
		bnplScript.src = this.environment.thirdParty.bnpl.scriptSrc;
		bnplScript.integrity = data['main.js'].integrity;
		bnplScript.crossOrigin = 'anonymous';
		bnplScript.async = true;
		bnplScript.type = 'text/javascript';
		this.document.head.appendChild(bnplScript);
		bnplScript.addEventListener('load', () => {
			this.offerMessaging(this.productPrice()?.minofferprice);
			this.remountWhenResolvedSkuChanges();
		});
	}

	offerMessaging(price: string) {
		const elementId = 'bnpl-messaging-container';
		const productDetail = {
			minAmount: this.productPrice()?.minofferprice,
			maxAmount: this.productPrice()?.maxofferprice,
			amount: price,
			isPaypalEligible: this.isPayPalEligible()
		};
		if (this.window?.dsgPaymentAdsJs !== undefined) {
			// Avoid app-stability delaying timeouts that live inside this script
			this.ngZone.runOutsideAngular(() => {
				this.mountPromise = this.mountPromise.then(() =>
					this.window.dsgPaymentAdsJs.mountOfferMessages(elementId, productDetail)
				);
			});
		}
	}

	remountWhenResolvedSkuChanges() {
		effect(
			() => {
				if (this.resolvedSku()?.prices) {
					this.offerMessaging(String(this.resolvedSku().prices.offerPrice));
				}
			},
			{ injector: this.injector }
		);
	}
}
